<template>
    <div>
        <base-wrapper
            :cardIcon="cardIcon"
            :cardText="cardText"
            :cardValue="cardValue"
            :layoutConfig="layoutConfig"
            :showText="showStatusText"
            :styles="getCardStyles"
            :widget="data"
            @show-modal="onShowModal"
            v-bind="$attrs"
            v-on="$listeners"
            usedDynamicStatuses
            :status="status"
            use-sound-animation
            :selected-type="0"
        />
        <update-dialog
            :layoutConfig="layoutConfig"
            :model="model"
            :visible.sync="showModal"
            @on-change="onChange"
            v-if="showModal">
            <template v-slot:header>
                <div class="flex items-center pt-4" :style="styleForStatusIconColor(selectedStatus)">
                    <component :is="selectedIcon" class="w-8 mx-1"/>
                    <p class="text-main-lg font-semibold text-gray-700" slot="title">{{ $t(selectedOption.text) }}</p>
                </div>
            </template>
            <template v-slot:content>
                <el-select
                    :placeholder="$t('common.selectStatus')"
                    @change="onStatusChange"
                    class="w-full"
                    label="select"
                    v-model="selectedStatus">
                    <el-option
                        :key="option.value"
                        :label="$t(option.text)"
                        v-bind="option"
                        v-for="option in statuses">
                        <div class="flex" :style="styleForStatusIconColor(option)">
                            <component :is="option.icon" class="w-5 mx-1 text-primary"/>
                            <span class="w-16 mx-1">{{ $t(option.text) }}</span>
                        </div>
                    </el-option>
                </el-select>
                <div class="mt-2 mb-2" v-if="useContentTitle">
                    <label class="main-text-color">{{ $t('widget.contentTitle') }}</label>
                    <el-input v-model="ContentTitle"/>
                </div>
                <div class="py-4 flex">
                    <el-checkbox v-model="showStatusText">
                        {{ $t('status.show.text') }}
                    </el-checkbox>
                    <el-checkbox class="px-4" v-model="displayItemBorder">
                        {{ $t('status.display.border') }}
                    </el-checkbox>
                </div>
            </template>
            <template v-slot:additional-data>
                <el-collapse v-model="activeCollapse" class="pt-2">
                    <el-collapse-item :title="$t('settings.sounds')" name="sounds">
                        <div class="flex justify-between mb-1">
                            <div>
                                <label class="main-text-color">{{ $t('widget.config.maximumWarningRangeValue') }}</label>:
                            </div>
                            <el-input-number
                                :max="100" :min="0" :step="1" type="number"
                                v-model="WarningMaxCounter"
                                size="small"
                            />
                        </div>
                        <div class="flex justify-between">
                            <div>
                                <label class="main-text-color">{{ $t('widget.config.maximumLimitRangeValue') }}</label>:
                            </div>
                            <el-input-number
                                :max="100" :min="0" :step="1" type="number"
                                v-model="LimitMaxCounter"
                                size="small"
                            />
                        </div>
                        <div class="flex flex-col">
                            <div class="flex justify-between mt-1">
                                <div class="color-block">
                                    <span class="text-main-sm whitespace-nowrap main-text-color">
                                        {{ $t('settings.warningColor') }}:
                                    </span>
                                </div>
                                <div class="flex items-center justify-end">
                                    <slot>
                                        <IconColorPicker
                                            class="w-5 h-5 icon-color-picker mx-2"
                                            :style="getIndicatorWarningColor"
                                        />
                                    </slot>
                                    <el-color-picker
                                        :class="$rtl.isRTL ? 'icon-color-picker-rtl' : 'icon-color-picker-ltr'"
                                        :predefine="predefinedColors"
                                        v-model="warningColor"
                                    />
                                </div>
                            </div>
                            <div class="flex justify-between mt-1">
                                <div class="color-block">
                                    <span class="text-main-sm whitespace-nowrap main-text-color">
                                        {{ $t('settings.limitColor') }}:
                                    </span>
                                </div>
                                <div class="flex items-center justify-end">
                                    <slot>
                                        <IconColorPicker
                                            class="w-5 h-5 icon-color-picker mx-2"
                                            :style="getIndicatorLimitColor"
                                        />
                                    </slot>
                                    <el-color-picker
                                        :class="$rtl.isRTL ? 'icon-color-picker-rtl' : 'icon-color-picker-ltr'"
                                        :predefine="predefinedColors"
                                        v-model="limitColor"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="py-2 flex justify-between items-center">
                            <div class="flex items-center">
                                <IconColorPicker
                                    class="w-5 h-5 icon-color-picker"
                                    :style="getIndicatorWarningColor"
                                />
                                <div class="flex items-center mx-2 text-main-sm whitespace-nowrap main-text-color">
                                    {{ $t('settings.warningSound') }}:
                                </div>
                            </div>
                            <SoundSelect
                                v-model="soundWarning"
                                :placeholder="$t('general.account')"
                                :options="warningSounds"
                            />
                        </div>
                        <div class="py-2 flex justify-between items-center">
                            <div class="flex items-center">
                                <IconColorPicker
                                    class="w-5 h-5 icon-color-picker"
                                    :style="getIndicatorLimitColor"
                                />
                                <div class="flex items-center mx-2 text-main-sm whitespace-nowrap main-text-color">
                                    {{ $t('settings.limitSound') }}:
                                </div>
                            </div>
                            <SoundSelect
                                v-model="soundLimit"
                                :placeholder="$t('general.account')"
                                :options="errorSounds"
                            />
                        </div>
                    </el-collapse-item>
                </el-collapse>
                <el-collapse class="pt-4" v-if="autoCompletes.length" v-model="activeCollapse">
                    <el-collapse-item :title="$t('settings.filters')" name="filters">
                        <auto-complete
                            :key="index"
                            :model="model.WidgetConfig[index]"
                            v-for="(filter, index) in model.WidgetConfig"
                            v-if="isAutoComplete(filter)"/>
                    </el-collapse-item>
                </el-collapse>
            </template>
            <template v-slot:footer>
                <div class="main-bg-light border-t-2 border-gray-300 py-4 px-10 flex items-center justify-between">
                    <cancel-button
                        @on-click="showModal = false"
                    />
                    <confirm-button
                        :label="$t('common.save')"
                        icon="IconSave"
                        @on-click="onChange"
                    />
                </div>
            </template>
        </update-dialog>
    </div>
</template>
<script>
    import { set } from 'vue'
    import get from 'lodash/get'
    import cloneDeep from 'lodash/cloneDeep'
    import extensionMixin from '@/mixins/extensionMixin'
    import { LOGOUT_STATUS } from '@/enum/extensionStatuses'
    import { defaultCardColors } from '@/enum/defaultWidgetSettings'
    import { Checkbox, Collapse, CollapseItem, Option, Select, Tooltip, InputNumber, ColorPicker } from 'element-ui'
    import statusTypes, {callStatuses, otherStatuses, statusCardsWithContentTitle} from '@/enum/statusTypes'
    import cardWidgetMixin from '@/mixins/cardWidgetMixin'
    import { predefinedColors } from '@/views/DashboardSettings/LayoutManagement/layout-management'
    import SoundSelector from '@/views/DashboardSettings/LayoutManagement/components/SoundSelector.vue'
    import SoundSelect from "@/components/Common/SoundSelect.vue";

    export default {
        mixins: [extensionMixin, cardWidgetMixin],
        props: {
            status: {
                type: Number,
                default: 3,
            },
            showText: {
                type: Boolean,
                default: true,
            },
            displayBorder: {
                type: Boolean,
                default: true,
            },
            data: {
                type: Object,
                default: () => ({}),
            },
        },
        components: {
            SoundSelect,
            UpdateDialog: () => import('./UpdateDialog'),
            AutoComplete: () => import('@/components/Widgets/WidgetUpdateForm/Filters/AutoComplete'),
            [Option.name]: Option,
            [Select.name]: Select,
            [Tooltip.name]: Tooltip,
            [Checkbox.name]: Checkbox,
            [Collapse.name]: Collapse,
            [CollapseItem.name]: CollapseItem,
            CancelButton: () => import("@/components/Common/Buttons/CancelButton"),
            ConfirmButton: () => import("@/components/Common/Buttons/ConfirmButton"),
            [InputNumber.name]: InputNumber,
            [ColorPicker.name]: ColorPicker,
            SoundSelector
        },
        data() {
            return {
                showModal: false,
                selectedStatus: '',
                selectedIcon: '',
                selectedOption: {},
                showStatusText: this.showText,
                displayItemBorder: this.displayBorder,
                model: {},
                layoutConfig: {},
                AUTO_COMPLETE_PARAMETER_TYPE: 6,
                USER_ID_LIST_KEY: '{|User_ID_id_list|}',
                activeCollapse: ['filters', 'sounds'],
                predefinedColors,
                limitColor: '',
                warningColor: '',
                soundWarning: 'none',
                soundLimit: 'none',
                WarningMaxCounter: 0,
                LimitMaxCounter: 0,
                ContentTitle: '',
            }
        },
        computed: {
            useContentTitle () {
                return statusCardsWithContentTitle.includes(this.data.TemplateID)
            },
            autoCompletes() {
                return this.model.WidgetConfig.filter(c => c.ParameterType === this.AUTO_COMPLETE_PARAMETER_TYPE)
            },
            statuses() {
                const storeStatuses = this.$store.getters['entities/accountStatuses']
                let localStatuses = Object.values(statusTypes)
                let finalStatuses = []

                if (storeStatuses.length) {
                    finalStatuses = this.getStoreStatuses()
                } else {
                    finalStatuses = localStatuses.map(status => {
                        const statusText = this.$store.getters['entities/getStatusTextById'](status.value)
                        return {
                            ...status,
                            text: statusText,
                        }
                    })
                }

                finalStatuses.push(statusTypes[callStatuses.CALLING])
                finalStatuses.push(statusTypes[callStatuses.HOLD])
                finalStatuses.push(statusTypes[otherStatuses.AT_WORK])

                return finalStatuses
            },
            userToDisplay() {
                return get(this.data.WidgetConfig, '[0].WidgetParameterValueJson.EntityPositive', [])
            },
            cardValue() {
                const userToDisplay = this.userToDisplay
                let extensionsList = []

                if (this.status === otherStatuses.AT_WORK) {
                    extensionsList = this.extensionWithCalls.filter(el => {
                        const fitsByStatus = el.representativeStatus !== LOGOUT_STATUS
                        const fitsBySelectedUsers = userToDisplay.length ? userToDisplay.includes(el.userID) : true

                        return fitsByStatus && fitsBySelectedUsers
                    })
                } else {
                    extensionsList = this.extensionWithCalls.filter(el => {
                        const fitsByStatus = el.representativeStatus === this.status
                        const fitsBySelectedUsers = userToDisplay.length ? userToDisplay.includes(el.userID) : true

                        return fitsByStatus && fitsBySelectedUsers
                    })
                }

                return extensionsList.length || '0'
            },
            cardIcon() {
                return statusTypes[this.status].icon
            },
            defaultCardText () {
                return this.$t(this.$store.getters['entities/getStatusTextById'](this.status))
            },
            cardText() {
                return this.data.WidgetLayout.ContentTitle || this.defaultCardText
            },
            textColor() {
                let color = statusTypes[this.status].color
                return {
                    color: `${color}`,
                }
            },
            getAccountStatuses () {
                return this.$store.getters['entities/accountStatuses']
            },
            getIndicatorLimitColor() {
                return {
                    'color': this.limitColor
                }
            },
            getIndicatorWarningColor() {
                return {
                    'color': this.warningColor
                }
            },
            warningSounds () {
                return this.$store.getters['entities/getWarningSounds']
            },
            errorSounds () {
                return this.$store.getters['entities/getErrorSounds']
            },
        },
        methods: {
            getStoreStatuses() {
                const storeStatuses = this.$store.getters['entities/accountStatuses']
                let localStatuses = Object.values(statusTypes)
                return storeStatuses.map(status => {
                    const otherData = localStatuses.find(s => s.value === status.StatusID) || {}
                    if (otherData) {
                        otherData['text'] = this.$store.getters['entities/getStatusTextById'](otherData.value)
                    }
                    return {
                        ...status,
                        ...otherData,
                    }
                })
            },
            onChange() {
                try {
                    this.model.WidgetConfig.forEach((config) => {
                        if (typeof config.WidgetParameterValueJson === 'object') {
                            config.WidgetParameterValueJson['AccountList'] = [this.$store.state.entities.selectedAccountID]
                        }
                        if (typeof config.WidgetParameterValue === 'object') {
                            config.WidgetParameterValue['AccountList'] = [this.$store.state.entities.selectedAccountID]
                            config.WidgetParameterValue = JSON.stringify(config.WidgetParameterValue)
                        }
                    })
                } catch (e) {
                    console.warn(e)
                }

                let data = {
                    status: this.selectedStatus,
                    showText: this.showStatusText,
                    displayBorder: this.displayItemBorder,
                    limitColor: this.limitColor,
                    warningColor: this.warningColor,
                    soundWarning: this.soundWarning,
                    soundLimit: this.soundLimit,
                    WarningMaxCounter: this.WarningMaxCounter,
                    LimitMaxCounter: this.LimitMaxCounter
                }

                this.data.WidgetLayout = {
                    ...this.data.WidgetLayout,
                    ...data,
                    ...this.layoutConfig,
                    colors: this.model.colors,
                };

                if (this.useContentTitle) {
                    set(this.data.WidgetLayout, 'ContentTitle', this.ContentTitle || this.defaultCardText)
                }

                this.data.WidgetConfig = this.model.WidgetConfig

                this.$emit('on-update', this.data);
                this.showModal = false;
            },
            onStatusChange(value) {
                let option = statusTypes[value];
                this.selectedOption = option;
                this.selectedStatus = option.value;
                this.selectedIcon = option.icon;
            },
            onShowModal() {
                this.showModal = true
            },
            isAutoComplete(WidgetConfig) {
                return WidgetConfig.ParameterType === this.AUTO_COMPLETE_PARAMETER_TYPE
            },
            styleForStatusIconColor (status) {
                const statusActive = typeof status === 'object' ? 'StatusID' in  status ? status.StatusID : status.value : status
                const dynamicColor = this.getAccountStatuses.find(el => Number(el.StatusID) === Number(statusActive))
                const color = dynamicColor && dynamicColor.ColorCode ? dynamicColor.ColorCode :  statusTypes[statusActive].color

                return {
                    '--status-svg-color': color
                }
            }
        },
        mounted() {
            this.selectedStatus = this.status;
            this.selectedOption = statusTypes[this.status];
            this.selectedIcon = this.selectedOption.icon;
        },
        watch: {
            data: {
                deep: true,
                immediate: true,
                handler: function (widget) {
                    this.model = cloneDeep(widget)
                    this.model.colors = cloneDeep(widget.WidgetLayout.colors || defaultCardColors)
                    if (this.useContentTitle) {
                        this.ContentTitle = this.model.WidgetLayout.ContentTitle || this.defaultCardText
                    }
                    this.limitColor = this.model.WidgetLayout.limitColor || ''
                    this.warningColor = this.model.WidgetLayout.warningColor || ''
                    this.soundWarning = this.model.WidgetLayout.soundWarning || 'none'
                    this.soundLimit = this.model.WidgetLayout.soundLimit || 'none'
                    this.WarningMaxCounter = this.model.WidgetLayout.WarningMaxCounter || 0
                    this.LimitMaxCounter = this.model.WidgetLayout.LimitMaxCounter || 0
                },
            },
        },
    }
</script>

<style scoped>
.color-block {
    width: 130px;
}</style>
